import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
  type ReactElement,
  type SetStateAction,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ExternalLink,
  Image as ImageIcon,
  Monitor,
  Plus,
  RefreshCw,
  Smartphone,
  Sparkles,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { API_BASE } from "@/lib/api";
import { SignalWaitDot } from "@/motion/SignalWaitDot";
import { signal } from "@/motion/signals";
import { useFreshIds } from "@/motion/use-fresh-ids";

import {
  continueWorkspaceRun,
  fetchWorkspace,
  fetchWorkspaceList,
  fetchWorkspaceMessages,
  sendWorkspaceMessage,
  WorkspaceApiError,
  type WorkspaceMessageAttachment,
  type WorkspaceMessage,
  type WorkspaceListItem,
} from "./api";
import { fetchBalance } from "../billing/api";
import { CreditBar } from "../billing/CreditBar";
import { RichMessage } from "./RichMessage";
import { WorkspaceFailureCard } from "./WorkspaceFailureCard";
import { WorkspaceFundingCard } from "./WorkspaceFundingCard";
import { WorkspacePreviewCard } from "./WorkspacePreviewCard";
import { WorkspaceProgressCard } from "./WorkspaceProgressCard";
import { WorkspaceSwitcher } from "./WorkspaceSwitcher";

type MobilePane = "chat" | "preview";
type PreviewSize = "desktop" | "mobile";
type WorkspaceErrorReason = "unauthorized" | "forbidden" | "unavailable";
type PendingAttachment = { file: File; previewUrl: string };
type CachedWorkspaceMessage = WorkspaceMessage & { optimistic?: true };
type WorkspaceMutationInput = {
  messageId: string;
  text: string;
  cloudJobBudgetCredits?: number;
  attachment?: PendingAttachment;
};
type WorkspaceContinuationInput = {
  messageId: string;
  runId: string;
  continuationId: string;
  cloudJobBudgetCredits: number;
  retry?: boolean;
};

const WORKSPACE_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/gif",
]);
const WORKSPACE_IMAGE_EXTENSION = /\.(?:jpe?g|png|webp|gif)$/i;
const MAX_WORKSPACE_IMAGE_BYTES = 4 * 1024 * 1024;
// The history endpoint returns the latest 100 append-only messages.
const WORKSPACE_HISTORY_LIMIT = 100;

function reconcileWorkspaceMessages(
  current: CachedWorkspaceMessage[] = [],
  fetched: WorkspaceMessage[],
): CachedWorkspaceMessage[] {
  const messages = new Map(
    current.map((message) => [message.messageId, message]),
  );
  for (const message of fetched) {
    const previous = messages.get(message.messageId);
    // A reply ends this message permanently (the next turn has a new ID).
    // Progress and final replies can carry timestamps from different records:
    // a late progress poll must not hide a final reply with an earlier timestamp.
    // Device timestamps on optimistic records never outrank server records.
    if (previous && !previous.optimistic) {
      const wasTerminal = ["complete", "failed"].includes(previous.status);
      const isTerminal = ["complete", "failed"].includes(message.status);
      const stale = previous.updatedAt > message.updatedAt &&
        !(!wasTerminal && isTerminal);
      const terminalRegression =
        wasTerminal && !isTerminal;
      if (stale || terminalRegression) continue;
    }
    messages.set(message.messageId, message);
  }
  return [...messages.values()]
    .sort((left, right) =>
      Number(Boolean(left.optimistic)) - Number(Boolean(right.optimistic)) ||
      left.createdAt.localeCompare(right.createdAt) ||
      left.messageId.localeCompare(right.messageId),
    )
    .slice(-WORKSPACE_HISTORY_LIMIT);
}

export function Workspace(): ReactElement {
  const { workspaceId } = useParams({ from: "/workspaces/$workspaceId" });
  const queryClient = useQueryClient();
  const [attachment, setAttachment] = useState<PendingAttachment | null>(null);
  const [mobilePane, setMobilePane] = useState<MobilePane>("chat");
  const [previewSize, setPreviewSize] = useState<PreviewSize>("desktop");
  const [previewKey, setPreviewKey] = useState(0);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitError, setSubmitError] = useState<{
    message: string;
    submittedText?: string;
  } | null>(null);
  const [continuationError, setContinuationError] = useState<{
    messageId: string;
    message: string;
  } | null>(null);
  const [retryingMessageId, setRetryingMessageId] = useState<string | null>(
    null,
  );
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);
  const threadViewport = useRef<HTMLDivElement>(null);
  const threadEnd = useRef<HTMLDivElement>(null);
  const followLatest = useRef(true);
  const composer = useRef<HTMLTextAreaElement>(null);
  const attachmentInput = useRef<HTMLInputElement>(null);
  const attachmentUrls = useRef(new Set<string>());
  const completedStamp = useRef<string | null | undefined>(undefined);

  const releaseAttachmentUrl = useCallback((url: string | undefined) => {
    if (!url || !attachmentUrls.current.delete(url)) return;
    URL.revokeObjectURL(url);
  }, []);

  useEffect(() => {
    const urls = attachmentUrls.current;
    return () => {
      for (const url of urls) URL.revokeObjectURL(url);
      urls.clear();
    };
  }, []);

  const workspaceQuery = useQuery({
    queryKey: ["workspace", workspaceId, "detail"],
    queryFn: () => fetchWorkspace(workspaceId),
    retry: false,
  });
  const siteScope = workspaceQuery.data;
  const [draft, setDraft] = useWorkspaceDraft(
    siteScope?.accountId && siteScope.workspaceId && siteScope.actorId
      ? JSON.stringify([API_BASE, siteScope.accountId, siteScope.workspaceId, siteScope.actorId, workspaceId])
      : null,
  );
  const canWrite = siteScope?.accessRole === "admin" || siteScope?.accessRole === "editor";
  const workspaceListQuery = useQuery({
    queryKey: ["workspaces"],
    queryFn: fetchWorkspaceList,
    staleTime: 60_000,
    retry: false,
  });
  const messagesQuery = useQuery({
    queryKey: ["workspace", workspaceId, "messages"],
    queryFn: async ({ signal }) => {
      const fetched = await fetchWorkspaceMessages(workspaceId, signal);
      // Read after the request finishes: a POST or another poll may have added
      // a newer message while this history snapshot was in flight.
      return reconcileWorkspaceMessages(
        queryClient.getQueryData<CachedWorkspaceMessage[]>([
          "workspace",
          workspaceId,
          "messages",
        ]),
        fetched,
      );
    },
    retry: false,
    staleTime: 5_000,
    // The browser can suspend polling while hidden or asleep. Read again on
    // return even if the cached transcript has no active message to poll.
    refetchOnWindowFocus: "always",
    refetchOnReconnect: "always",
    refetchInterval: (query) =>
      query.state.data?.some((message) =>
        ["processing", "working", "continuing"].includes(message.status),
      )
        ? 2_000
        : false,
  });
  const messages = messagesQuery.data ?? [];
  const messageIds = useMemo(
    () => messages.map((message) => message.messageId),
    [messages],
  );
  const freshIds = useFreshIds(messageIds, messagesQuery.isSuccess);
  const hasActiveWork = messages.some((message) =>
    ["processing", "working", "continuing"].includes(message.status),
  );
  const latestCompleted = [...messages]
    .reverse()
    .find((message) => message.status === "complete");
  const conversationRefreshError = messagesQuery.isRefetchError
    ? messagesQuery.error
    : null;

  useEffect(() => {
    if (!followLatest.current) {
      setShowJumpToLatest(true);
      return;
    }
    setShowJumpToLatest(false);
    threadEnd.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length, messages.at(-1)?.status]);

  useEffect(() => {
    if (!composer.current) return;
    composer.current.style.height = "auto";
    composer.current.style.height = `${Math.min(composer.current.scrollHeight, 192)}px`;
  }, [draft]);

  useEffect(() => {
    if (!workspaceQuery.data) return;
    const previousTitle = document.title;
    document.title = `${workspaceQuery.data.name} — Chill`;
    return () => {
      document.title = previousTitle;
    };
  }, [workspaceQuery.data]);

  const mutation = useMutation({
    mutationFn: (input: WorkspaceMutationInput) =>
      sendWorkspaceMessage(workspaceId, {
        messageId: input.messageId,
        text: input.text,
        cloudJobBudgetCredits: input.cloudJobBudgetCredits,
        attachment: input.attachment?.file,
      }),
    onMutate: async (input) => {
      setSubmitError(null);
      await queryClient.cancelQueries({
        queryKey: ["workspace", workspaceId, "messages"],
      });
      const timestamp = new Date().toISOString();
      queryClient.setQueryData<CachedWorkspaceMessage[]>(
        ["workspace", workspaceId, "messages"],
        (current = []) => [
          ...current,
          {
            messageId: input.messageId,
            text: input.text,
            status: "processing",
            cloudJobBudgetCredits: input.cloudJobBudgetCredits,
            attachments: input.attachment
              ? [
                  {
                    kind: "image",
                    filename: input.attachment.file.name,
                    contentType: input.attachment.file.type,
                    size: input.attachment.file.size,
                    url: input.attachment.previewUrl,
                  },
                ]
              : undefined,
            createdAt: timestamp,
            updatedAt: timestamp,
            optimistic: true,
          },
        ],
      );
      return {
        submittedText: input.text,
        submittedAttachment: input.attachment,
      };
    },
    onSuccess: (message, input) => {
      releaseAttachmentUrl(input.attachment?.previewUrl);
      queryClient.setQueryData<CachedWorkspaceMessage[]>(
        ["workspace", workspaceId, "messages"],
        (current) => reconcileWorkspaceMessages(current, [message]),
      );
    },
    onError: (error, input, context) => {
      const accepted = queryClient
        .getQueryData<CachedWorkspaceMessage[]>([
          "workspace", workspaceId, "messages",
        ])
        ?.find((item) => item.messageId === input.messageId && !item.optimistic);
      if (accepted) {
        // A poll can confirm receipt even when the POST response is lost.
        // Keep that server state rather than inviting a duplicate submission.
        releaseAttachmentUrl(input.attachment?.previewUrl);
        return;
      }
      queryClient.setQueryData<CachedWorkspaceMessage[]>(
        ["workspace", workspaceId, "messages"],
        (current = []) =>
          current.filter((item) =>
            item.messageId !== input.messageId || !item.optimistic,
          ),
      );
      setDraft((current) =>
        current.trim() ? current : (context?.submittedText ?? ""),
      );
      if (context?.submittedAttachment) {
        setAttachment((current) => {
          if (current) {
            releaseAttachmentUrl(input.attachment?.previewUrl);
            return current;
          }
          return context.submittedAttachment ?? null;
        });
      }
      setSubmitError({
        message: workspaceErrorMessage(error),
        submittedText: input.text,
      });
      if (error instanceof WorkspaceApiError && error.status === 409) {
        // Another tab or a previously accepted request may own the server's
        // work lock. Show that work and resume polling without losing the draft.
        void queryClient.invalidateQueries({
          queryKey: ["workspace", workspaceId, "messages"],
        });
      }
    },
  });

  const continuationMutation = useMutation({
    mutationFn: (input: WorkspaceContinuationInput) =>
      continueWorkspaceRun(workspaceId, input.runId, {
        continuationId: input.continuationId,
        cloudJobBudgetCredits: input.cloudJobBudgetCredits,
        ...(input.retry ? { retry: true } : {}),
      }),
    onMutate: (input) => {
      setContinuationError((current) =>
        current?.messageId === input.messageId ? null : current,
      );
    },
    onSuccess: (message) => {
      queryClient.setQueryData<WorkspaceMessage[]>(
        ["workspace", workspaceId, "messages"],
        (current = []) =>
          current.map((item) =>
            item.messageId === message.messageId ? message : item,
          ),
      );
      void queryClient.invalidateQueries({ queryKey: ["credit-balance"] });
    },
    onError: (error, input) => {
      setContinuationError({
        messageId: input.messageId,
        message: continuationErrorMessage(error),
      });
    },
  });
  const workspaceBusy = hasActiveWork || continuationMutation.isPending;

  const previewUrl = useMemo(() => {
    const latestPreview = [...messages]
      .reverse()
      .find((message) => message.status === "complete" && message.previewUrl);
    return latestPreview?.previewUrl ?? workspaceQuery.data?.previewUrl ?? null;
  }, [messages, workspaceQuery.data?.previewUrl]);

  const latestCompletedStamp = latestCompleted
    ? `${latestCompleted.messageId}:${latestCompleted.updatedAt}`
    : null;

  useEffect(() => {
    if (!messagesQuery.isSuccess) return;
    if (completedStamp.current === undefined) {
      completedStamp.current = latestCompletedStamp;
      return;
    }
    if (
      latestCompletedStamp === null ||
      completedStamp.current === latestCompletedStamp
    ) {
      return;
    }
    completedStamp.current = latestCompletedStamp;
    setPreviewLoading(Boolean(previewUrl));
    setPreviewKey((key) => key + 1);
  }, [latestCompletedStamp, messagesQuery.isSuccess, previewUrl]);

  useEffect(() => {
    setPreviewLoading(Boolean(previewUrl));
  }, [previewUrl]);

  function submit(event: FormEvent): void {
    event.preventDefault();
    const text = draft.trim();
    if (!canWrite || !text || mutation.isPending || workspaceBusy) return;
    const submittedAttachment = attachment ?? undefined;
    setDraft("");
    setAttachment(null);
    if (attachmentInput.current) attachmentInput.current.value = "";
    jumpToLatest();
    mutation.mutate({
      messageId: crypto.randomUUID(),
      text,
      attachment: submittedAttachment,
    });
  }

  async function retry(message: WorkspaceMessage): Promise<void> {
    if (!canWrite || mutation.isPending || workspaceBusy || retryingMessageId) return;
    setRetryingMessageId(message.messageId);
    setSubmitError(null);
    let submissionStarted = false;
    let submittedAttachment: PendingAttachment | undefined;
    try {
      const persistedAttachment = message.attachments?.[0];
      if (persistedAttachment) {
        const url = safeAttachmentUrl(persistedAttachment.url);
        if (!url) throw new Error("persisted attachment URL is unavailable");
        const response = await fetch(url, {
          cache: "no-store",
          credentials: "omit",
          referrerPolicy: "no-referrer",
        });
        if (!response.ok)
          throw new Error("persisted attachment download failed");
        const blob = await response.blob();
        const contentType = (
          response.headers.get("content-type") ??
          persistedAttachment.contentType
        )
          .toLowerCase()
          .split(";")[0]!
          .trim();
        if (
          !WORKSPACE_IMAGE_TYPES.has(contentType) ||
          blob.size === 0 ||
          blob.size > MAX_WORKSPACE_IMAGE_BYTES
        ) {
          throw new Error("persisted attachment is invalid");
        }
        const file = new File([blob], persistedAttachment.filename, {
          type: contentType,
        });
        const previewUrl = URL.createObjectURL(file);
        attachmentUrls.current.add(previewUrl);
        submittedAttachment = { file, previewUrl };
      }

      jumpToLatest();
      submissionStarted = true;
      await mutation.mutateAsync({
        messageId: crypto.randomUUID(),
        text: message.text,
        cloudJobBudgetCredits: message.cloudJobBudgetCredits,
        attachment: submittedAttachment,
      });
    } catch {
      if (!submissionStarted) {
        releaseAttachmentUrl(submittedAttachment?.previewUrl);
        setSubmitError({
          message: "That image couldn’t be reattached. Please attach it again.",
        });
      }
    } finally {
      setRetryingMessageId(null);
    }
  }

  function continueRun(
    message: WorkspaceMessage,
    continuationId: string,
    cloudJobBudgetCredits: number,
  ): void {
    if (
      !canWrite ||
      continuationMutation.isPending ||
      !["needs_more_credits", "continuing"].includes(message.status) ||
      !message.workId
    ) {
      return;
    }
    continuationMutation.mutate({
      messageId: message.messageId,
      runId: message.workId,
      continuationId,
      cloudJobBudgetCredits,
      ...(message.status === "continuing" ? { retry: true } : {}),
    });
  }

  function chooseAttachment(event: ChangeEvent<HTMLInputElement>): void {
    if (!canWrite) return;
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = "";
    if (!file) return;
    const contentType = file.type.toLowerCase().split(";")[0]!.trim();
    const hasExtension = /\.[^.]+$/.test(file.name);
    if (
      !WORKSPACE_IMAGE_TYPES.has(contentType) ||
      (hasExtension && !WORKSPACE_IMAGE_EXTENSION.test(file.name))
    ) {
      setSubmitError({ message: "Choose a JPEG, PNG, WebP, or GIF image." });
      return;
    }
    if (file.size === 0) {
      setSubmitError({ message: "That image is empty. Choose another one." });
      return;
    }
    if (file.size > MAX_WORKSPACE_IMAGE_BYTES) {
      setSubmitError({ message: "Choose an image that is 4 MB or smaller." });
      return;
    }
    const previewUrl = URL.createObjectURL(file);
    attachmentUrls.current.add(previewUrl);
    setAttachment((current) => {
      releaseAttachmentUrl(current?.previewUrl);
      return { file, previewUrl };
    });
    setSubmitError(null);
  }

  function removeAttachment(): void {
    setAttachment((current) => {
      releaseAttachmentUrl(current?.previewUrl);
      return null;
    });
    if (attachmentInput.current) attachmentInput.current.value = "";
  }

  function updateFollowLatest(): void {
    const viewport = threadViewport.current;
    if (!viewport) return;
    const distanceFromBottom =
      viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
    const nearLatest = distanceFromBottom <= 96;
    followLatest.current = nearLatest;
    setShowJumpToLatest(!nearLatest);
  }

  function jumpToLatest(): void {
    followLatest.current = true;
    setShowJumpToLatest(false);
    threadEnd.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }

  function refreshPreview(): void {
    if (!previewUrl) return;
    setPreviewLoading(true);
    setPreviewKey((key) => key + 1);
  }

  if (workspaceQuery.isPending || messagesQuery.isPending) {
    return <WorkspaceLoading />;
  }
  const error =
    (!workspaceQuery.data ? workspaceQuery.error : null) ??
    (messagesQuery.data === undefined ? messagesQuery.error : null);
  if (error) {
    const status = error instanceof WorkspaceApiError ? error.status : null;
    return (
      <WorkspaceError
        workspaceId={workspaceId}
        reason={
          status === 401
            ? "unauthorized"
            : status === 403
              ? "forbidden"
              : "unavailable"
        }
      />
    );
  }
  const workspace = workspaceQuery.data;
  if (!workspace) return <WorkspaceError workspaceId={workspaceId} />;
  const isIosApp = workspace.artifact.kind === "ios_app";
  const activePreviewSize: PreviewSize = isIosApp ? "mobile" : previewSize;

  return (
    <div className="flex h-full flex-col overflow-hidden bg-[#f4f4f1] text-[#171816]">
      <WorkspaceHeader
        current={workspace}
        previewUrl={previewUrl}
        workspaces={workspaceListQuery.data ?? [workspace]}
      />

      <div className="flex border-b border-[#deded8] bg-white px-3 py-2 lg:hidden">
        <MobilePaneButton
          active={mobilePane === "chat"}
          onClick={() => setMobilePane("chat")}
        >
          Chat
        </MobilePaneButton>
        <MobilePaneButton
          active={mobilePane === "preview"}
          busy={workspaceBusy}
          onClick={() => setMobilePane("preview")}
        >
          Preview
        </MobilePaneButton>
      </div>

      <main className="grid min-h-0 flex-1 lg:grid-cols-[minmax(340px,420px)_minmax(0,1fr)]">
        <section
          className={cn(
            "min-h-0 min-w-0 flex-col border-r border-[#deded8] bg-white",
            mobilePane === "chat" ? "flex" : "hidden",
            "lg:flex",
          )}
          aria-label="Workspace conversation"
        >
          <div className="border-b border-[#ecece7] px-5 py-4">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Sparkles className="size-4 text-[#49756b]" aria-hidden="true" />
              {canWrite ? "Create with Chill" : "Conversation"}
            </div>
            <p className="mt-1 text-xs leading-5 text-[#74766f]">
              {canWrite
                ? "Describe what you want. Chill will make the change and show you the result."
                : "View the conversation and preview. You have view-only access."}
            </p>
          </div>

          <div className="relative min-h-0 flex-1">
            <div
              ref={threadViewport}
              className="h-full overflow-y-auto px-4 py-6 sm:px-5"
              onScroll={updateFollowLatest}
            >
              {conversationRefreshError ? (
                <ConversationRefreshNotice
                  error={conversationRefreshError}
                  workspaceId={workspaceId}
                  retrying={messagesQuery.isFetching}
                  onRetry={() => void messagesQuery.refetch()}
                />
              ) : null}
              {messages.length === 0 ? (
                <EmptyConversation
                  name={workspace.name}
                  artifactKind={workspace.artifact.kind}
                  onChoosePrompt={canWrite ? (prompt) => {
                    setDraft(prompt);
                    composer.current?.focus();
                  } : undefined}
                />
              ) : null}
              <div className="space-y-7" aria-live="polite">
                {messages.map((message) => (
                  <ConversationTurn
                    key={message.messageId}
                    message={message}
                    enter={freshIds.has(message.messageId)}
                    retryDisabled={
                      mutation.isPending ||
                      workspaceBusy ||
                      retryingMessageId !== null
                    }
                    onRetry={() => void retry(message)}
                    canWrite={canWrite}
                    continuationBusy={
                      continuationMutation.isPending &&
                      continuationMutation.variables?.messageId ===
                        message.messageId
                    }
                    continuationError={
                      continuationError?.messageId === message.messageId
                        ? continuationError.message
                        : undefined
                    }
                    onContinue={(continuationId, credits) =>
                      continueRun(message, continuationId, credits)
                    }
                    hasPreview={Boolean(previewUrl)}
                    onViewPreview={
                      !workspaceBusy &&
                      previewUrl &&
                      latestCompleted?.messageId === message.messageId
                        ? () => setMobilePane("preview")
                        : undefined
                    }
                  />
                ))}
                <div ref={threadEnd} />
              </div>
            </div>
            {showJumpToLatest ? (
              <button
                type="button"
                onClick={jumpToLatest}
                className={cn(
                  "absolute bottom-3 left-1/2 z-10 inline-flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-[#d5ddd9] bg-white px-3 py-1.5 text-xs font-medium text-[#3f5f57] shadow-[0_8px_24px_rgba(36,39,34,0.14)] hover:bg-[#f5f8f7]",
                  signal.arrive,
                )}
              >
                <ArrowDown className="size-3.5" aria-hidden="true" />
                Jump to latest
              </button>
            ) : null}
          </div>

          <form
            onSubmit={submit}
            className="border-t border-[#e7e7e1] bg-white p-3 sm:p-4"
          >
            <div className="rounded-2xl border border-[#d8d8d1] bg-white p-2 shadow-[0_8px_30px_rgba(36,39,34,0.08)] focus-within:border-[#9cafaa] focus-within:ring-2 focus-within:ring-[#dce8e5]">
              <input
                ref={attachmentInput}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={chooseAttachment}
                disabled={!canWrite}
                className="sr-only"
                aria-label="Choose an image"
              />
              {attachment ? (
                <ComposerAttachment
                  attachment={attachment}
                  onRemove={removeAttachment}
                />
              ) : null}
              <label htmlFor="workspace-message" className="sr-only">
                Message Chill
              </label>
              <textarea
                ref={composer}
                id="workspace-message"
                value={draft}
                disabled={!canWrite}
                onChange={(event) => setDraft(event.currentTarget.value)}
                onKeyDown={(event) => {
                  if (
                    event.key !== "Enter" ||
                    event.shiftKey ||
                    event.nativeEvent.isComposing
                  ) {
                    return;
                  }
                  event.preventDefault();
                  event.currentTarget.form?.requestSubmit();
                }}
                rows={1}
                maxLength={4_000}
                placeholder="Describe what you want to make or change…"
                aria-describedby="workspace-message-hint"
                className="block max-h-48 min-h-14 w-full resize-none overflow-y-auto bg-transparent px-2 py-1.5 text-sm leading-6 outline-none placeholder:text-[#9a9b94]"
              />
              <div className="flex items-center justify-between gap-3 px-1 pb-0.5">
                <div className="flex min-w-0 items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => attachmentInput.current?.click()}
                    disabled={!canWrite || mutation.isPending}
                    className="flex size-8 shrink-0 items-center justify-center rounded-full text-[#6e716a] transition-colors hover:bg-[#f0f2ef] hover:text-[#20272b] disabled:cursor-not-allowed disabled:opacity-45"
                    aria-label="Attach an image"
                  >
                    <Plus className="size-5" aria-hidden="true" />
                  </button>
                  <span
                    id="workspace-message-hint"
                    className="truncate text-[11px] text-[#96988f]"
                    aria-live="polite"
                  >
                    {!canWrite ? "View-only access to this workspace" : workspaceBusy
                      ? "Draft your next request while Chill finishes"
                      : "Enter to send · Shift + Enter for a new line"}
                  </span>
                </div>
                <Button
                  type="submit"
                  size="icon"
                  disabled={
                    !canWrite || !draft.trim() || mutation.isPending || workspaceBusy
                  }
                  className="size-8 shrink-0 rounded-full bg-[#20272b] text-white hover:bg-[#111719]"
                  aria-label={
                    workspaceBusy ? "Chill is working" : "Send message"
                  }
                >
                  {mutation.isPending || workspaceBusy ? (
                    <SignalWaitDot className="bg-white" />
                  ) : (
                    <ArrowUp aria-hidden="true" />
                  )}
                </Button>
              </div>
            </div>
            {submitError ? (
              <div className="mt-2 px-2 text-xs text-red-700" role="alert">
                <p>{submitError.message}</p>
                {submitError.submittedText &&
                draft.trim() !== submitError.submittedText ? (
                  <p className="mt-1 max-h-24 select-text overflow-y-auto whitespace-pre-wrap break-words text-[#5e6158]">
                    Previous request: {submitError.submittedText}
                  </p>
                ) : null}
              </div>
            ) : null}
          </form>
        </section>

        <section
          className={cn(
            "min-h-0 min-w-0 flex-col bg-[#eeeeea]",
            mobilePane === "preview" ? "flex" : "hidden",
            "lg:flex",
          )}
          aria-label="Current preview"
        >
          <div className="flex h-12 shrink-0 items-center justify-between border-b border-[#deded8] bg-white px-3">
            <div className="flex min-w-0 items-center gap-2.5">
              {isIosApp ? (
                <span className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-[#f1f1ed] px-2.5 text-xs font-medium text-[#59605b]">
                  <Smartphone className="size-3.5" aria-hidden="true" />
                  iPhone
                </span>
              ) : (
                <div className="flex items-center gap-1 rounded-lg bg-[#f1f1ed] p-1">
                  <PreviewSizeButton
                    active={activePreviewSize === "desktop"}
                    label="Desktop preview"
                    onClick={() => setPreviewSize("desktop")}
                  >
                    <Monitor />
                  </PreviewSizeButton>
                  <PreviewSizeButton
                    active={activePreviewSize === "mobile"}
                    label="Mobile preview"
                    onClick={() => setPreviewSize("mobile")}
                  >
                    <Smartphone />
                  </PreviewSizeButton>
                </div>
              )}
              {workspaceBusy ? (
                <span
                  className="inline-flex min-w-0 items-center gap-1.5 truncate text-xs text-[#6d7169]"
                  aria-live="polite"
                >
                  <SignalWaitDot className="bg-[#6d7169]" />
                  {isIosApp ? "Preparing iPhone app…" : "Updating preview…"}
                </span>
              ) : previewLoading && previewUrl ? (
                <span
                  className="inline-flex min-w-0 items-center gap-1.5 truncate text-xs text-[#6d7169]"
                  aria-live="polite"
                >
                  <SignalWaitDot className="bg-[#6d7169]" />
                  Loading preview…
                </span>
              ) : null}
            </div>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8"
                onClick={refreshPreview}
                aria-label={
                  isIosApp ? "Refresh iPhone preview" : "Refresh preview"
                }
                disabled={!previewUrl}
              >
                <RefreshCw className={cn(previewLoading && "animate-spin")} />
              </Button>
              {previewUrl ? (
                <Button
                  asChild
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  aria-label={
                    isIosApp
                      ? "Open iPhone preview in a new tab"
                      : "Open preview in a new tab"
                  }
                >
                  <a href={previewUrl} target="_blank" rel="noreferrer">
                    <ExternalLink />
                  </a>
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  aria-label={
                    isIosApp
                      ? "Open iPhone preview in a new tab"
                      : "Open preview in a new tab"
                  }
                  disabled
                >
                  <ExternalLink />
                </Button>
              )}
            </div>
          </div>
          <div className="flex min-h-0 flex-1 items-stretch justify-center overflow-auto p-3 sm:p-5">
            {previewUrl ? (
              <div
                className={cn(
                  "relative h-full overflow-hidden bg-white shadow-[0_18px_60px_rgba(38,40,35,0.15)] transition-[width,border-radius] duration-300",
                  activePreviewSize === "mobile"
                    ? "w-[390px] max-w-full rounded-[28px] border-[7px] border-[#20211f]"
                    : "w-full rounded-xl border border-[#d7d7d1]",
                )}
              >
                <iframe
                  key={`${previewUrl}-${previewKey}`}
                  src={previewUrl}
                  title={`${workspace.name} preview`}
                  className="h-full w-full border-0 bg-white"
                  referrerPolicy="strict-origin-when-cross-origin"
                  onLoad={() => setPreviewLoading(false)}
                />
                {previewLoading ? (
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center gap-2 bg-white/85 text-xs font-medium text-[#6d7169] backdrop-blur-[1px]">
                    <SignalWaitDot className="bg-[#6d7169]" />
                    Loading preview…
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="m-auto max-w-sm text-center text-sm text-[#777970]">
                {isIosApp
                  ? "An iPhone preview will appear here when one is available."
                  : "The first preview will appear here when it is ready."}
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}

/**
 * Credit readout in the workspace header. Rendered only once the balance
 * resolves — a billing widget is not worth a spinner or an error state in the
 * chrome of the page the customer came here to use.
 */
function WorkspaceCreditBar(): ReactElement | null {
  const balanceQuery = useQuery({
    queryKey: ["credit-balance"],
    queryFn: fetchBalance,
    retry: false,
    staleTime: 30_000,
  });
  if (!balanceQuery.data) return null;
  return <CreditBar balance={balanceQuery.data} className="hidden sm:flex" />;
}

function WorkspaceHeader({
  current,
  previewUrl,
  workspaces,
}: {
  current: WorkspaceListItem;
  previewUrl: string | null;
  workspaces: WorkspaceListItem[];
}): ReactElement {
  const isIosApp = current.artifact.kind === "ios_app";
  const openLabel = isIosApp ? "Open iPhone preview" : "Open preview";
  return (
    <header className="flex h-14 shrink-0 items-center justify-between gap-2 border-b border-[#deded8] bg-white px-3 sm:px-4">
      <div className="flex min-w-0 flex-1 items-center gap-1.5">
        <Button
          asChild
          variant="ghost"
          size="sm"
          className="shrink-0 px-2 sm:px-3"
        >
          <a href="/workspaces" aria-label="Back to projects">
            <ArrowLeft aria-hidden="true" />
            <span className="hidden md:inline">Projects</span>
          </a>
        </Button>
        <WorkspaceSwitcher current={current} workspaces={workspaces} />
      </div>
      <WorkspaceCreditBar />
      {previewUrl ? (
        <Button
          asChild
          variant="outline"
          size="sm"
          className="shrink-0 px-2 sm:px-3"
        >
          <a
            href={previewUrl}
            target="_blank"
            rel="noreferrer"
            aria-label={`${openLabel} in a new tab`}
          >
            <span className="hidden sm:inline">{openLabel}</span>
            <ExternalLink />
          </a>
        </Button>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0 px-2 sm:px-3"
          aria-label={
            isIosApp ? "iPhone preview is not ready" : "Preview is not ready"
          }
          disabled
        >
          <span className="hidden sm:inline">{openLabel}</span>
          <ExternalLink />
        </Button>
      )}
    </header>
  );
}

function EmptyConversation({
  name,
  artifactKind,
  onChoosePrompt,
}: {
  name: string;
  artifactKind: WorkspaceListItem["artifact"]["kind"];
  onChoosePrompt?: (prompt: string) => void;
}): ReactElement {
  const prompts =
    artifactKind === "ios_app"
      ? [
          "Make the first screen feel more polished",
          "Improve the onboarding flow",
          "Add a new feature for…",
        ]
      : [
          "Make the homepage feel more polished",
          "Improve the mobile layout",
          "Add a new section for…",
        ];
  return (
    <div className="mb-8 rounded-2xl border border-[#e4e8e5] bg-[#f5f8f7] p-4 text-sm leading-6 text-[#3e4b47]">
      <div className="mb-2 flex items-center gap-2 font-semibold text-[#20272b]">
        <span className="flex size-6 items-center justify-center rounded-full bg-[#dce9e5]">
          <Sparkles className="size-3.5" aria-hidden="true" />
        </span>
        Chill
      </div>
      {onChoosePrompt
        ? `This is the workspace for ${name}. Tell me what you want to create, change, or improve.`
        : `No messages yet for ${name}. New messages will appear here.`}
      {onChoosePrompt ? <div className="mt-4 flex flex-wrap gap-2" aria-label="Starter prompts">
        {prompts.map((prompt) => (
          <button
            key={prompt}
            type="button"
            onClick={() => onChoosePrompt(prompt)}
            className="rounded-full border border-[#cfdbd7] bg-white px-3 py-1.5 text-left text-xs font-medium leading-5 text-[#45645d] transition-colors hover:border-[#aebfba] hover:bg-[#f9fbfa]"
          >
            {prompt}
          </button>
        ))}
      </div> : null}
    </div>
  );
}

function ComposerAttachment({
  attachment,
  onRemove,
}: {
  attachment: PendingAttachment;
  onRemove: () => void;
}): ReactElement {
  return (
    <div className="mb-2 flex items-center gap-2 rounded-xl bg-[#f2f4f1] p-2 pr-1.5">
      <img
        src={attachment.previewUrl}
        alt="Selected image"
        className="size-12 shrink-0 rounded-lg object-cover"
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-medium text-[#373a35]">
          {attachment.file.name}
        </p>
        <p className="text-[11px] text-[#85887f]">
          Image · {formatFileSize(attachment.file.size)}
        </p>
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="flex size-8 shrink-0 items-center justify-center rounded-full text-[#74776f] hover:bg-white hover:text-[#333631]"
        aria-label={`Remove ${attachment.file.name}`}
      >
        <X className="size-4" aria-hidden="true" />
      </button>
    </div>
  );
}

function MessageAttachments({
  attachments,
}: {
  attachments: WorkspaceMessageAttachment[] | undefined;
}): ReactElement | null {
  if (!attachments?.length) return null;
  return (
    <div className="space-y-2">
      {attachments.map((attachment, index) => {
        const url = safeAttachmentUrl(attachment.url);
        return url ? (
          <figure key={`${attachment.filename}-${index}`} className="max-w-60">
            <img
              src={url}
              alt={`Attached image: ${attachment.filename}`}
              className="max-h-64 w-full rounded-xl object-cover"
              loading="lazy"
              referrerPolicy="no-referrer"
            />
            <figcaption className="mt-1 truncate px-1 text-[11px] text-[#6f726b]">
              {attachment.filename}
            </figcaption>
          </figure>
        ) : (
          <div
            key={`${attachment.filename}-${index}`}
            className="flex max-w-60 items-center gap-2 rounded-xl bg-white/60 px-2.5 py-2"
          >
            <ImageIcon
              className="size-4 shrink-0 text-[#777a73]"
              aria-hidden="true"
            />
            <span className="truncate text-xs text-[#555851]">
              {attachment.filename}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function ConversationRefreshNotice({
  error,
  workspaceId,
  retrying,
  onRetry,
}: {
  error: Error;
  workspaceId: string;
  retrying: boolean;
  onRetry: () => void;
}): ReactElement {
  const status = error instanceof WorkspaceApiError ? error.status : null;
  const sessionExpired = status === 401;
  const accessLost = status === 403;
  return (
    <div
      className="sticky top-0 z-10 mb-5 flex items-center justify-between gap-3 rounded-xl border border-[#e2d8c8] bg-[#fffaf2] px-3 py-2.5 text-xs text-[#66543d] shadow-sm"
      role="alert"
    >
      <span>
        {sessionExpired
          ? "Your session expired. Sign in to keep working."
          : accessLost
            ? "You no longer have access to this workspace."
            : retrying
              ? "Reconnecting to this conversation…"
              : "Conversation updates paused. Your messages are still here."}
      </span>
      {sessionExpired || accessLost ? (
        <a
          href={
            sessionExpired
              ? `/login?slug=${encodeURIComponent(workspaceId)}`
              : "/workspaces"
          }
          className="shrink-0 font-semibold underline underline-offset-2"
        >
          {sessionExpired ? "Sign in" : "Workspaces"}
        </a>
      ) : (
        <button
          type="button"
          disabled={retrying}
          onClick={onRetry}
          className="shrink-0 font-semibold underline underline-offset-2 disabled:no-underline disabled:opacity-60"
        >
          {retrying ? "Retrying…" : "Retry"}
        </button>
      )}
    </div>
  );
}

function ConversationTurn({
  message,
  enter = false,
  retryDisabled,
  canWrite,
  onRetry,
  continuationBusy,
  continuationError,
  onContinue,
  hasPreview,
  onViewPreview,
}: {
  message: WorkspaceMessage;
  enter?: boolean;
  retryDisabled: boolean;
  canWrite: boolean;
  onRetry: () => void;
  continuationBusy: boolean;
  continuationError?: string;
  onContinue: (continuationId: string, credits: number) => void;
  hasPreview: boolean;
  onViewPreview?: () => void;
}): ReactElement {
  const waiting =
    message.status === "processing" || message.status === "working";
  const wasWaiting = useRef(waiting);
  const [settled, setSettled] = useState(false);
  const [generatedContinuationId] = useState(
    () => `${message.messageId}:continue:${crypto.randomUUID()}`,
  );
  const continuationId =
    message.continuationId ?? generatedContinuationId;
  useEffect(() => {
    if (wasWaiting.current && !waiting) setSettled(true);
    wasWaiting.current = waiting;
  }, [waiting]);
  const replyEnter = enter || settled;

  return (
    <article className="space-y-4">
      <div
        className={cn(
          "ml-auto max-w-[88%] break-words rounded-2xl rounded-br-md bg-[#ecece7] px-3 py-3 text-sm leading-6 text-[#252622]",
          enter && signal.arrive,
        )}
      >
        <MessageAttachments attachments={message.attachments} />
        <div
          className={cn(
            "whitespace-pre-wrap px-1",
            message.attachments?.length ? "mt-2" : undefined,
          )}
        >
          {message.text}
        </div>
      </div>
      {waiting ? (
        <WorkspaceProgressCard
          status={message.status === "processing" ? "processing" : "working"}
          progress={message.progress}
          todos={message.todos}
        />
      ) : (
        <div
          className={cn(
            "flex max-w-[94%] gap-2.5",
            replyEnter && signal.arrive,
          )}
        >
          <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-[#dce9e5] text-[#315d54]">
            <Sparkles className="size-3.5" aria-hidden="true" />
          </span>
          <div className="min-w-0 break-words pt-0.5 text-sm leading-6 text-[#373a35]">
            {message.status === "failed" ? (
              <WorkspaceFailureCard
                explanation={message.reply}
                hasPreview={hasPreview}
                retryDisabled={!canWrite || retryDisabled}
                onRetry={onRetry}
                enter={replyEnter}
              />
            ) : ["needs_more_credits", "continuing"].includes(
                message.status,
              ) ? (
              <div>
                <RichMessage>{message.reply ?? ""}</RichMessage>
                {canWrite ? <WorkspaceFundingCard
                  suggestedCredits={message.suggestedContinuationCredits}
                  busy={continuationBusy}
                  error={continuationError}
                  recovering={message.status === "continuing"}
                  onContinue={(credits) =>
                    onContinue(continuationId, credits)
                  }
                /> : null}
              </div>
            ) : (
              <div>
                <RichMessage>{message.reply ?? ""}</RichMessage>
                {message.previewUrl ? (
                  <WorkspacePreviewCard
                    url={message.previewUrl}
                    onViewPreview={onViewPreview}
                    enter={replyEnter}
                  />
                ) : onViewPreview ? (
                  <button
                    type="button"
                    onClick={onViewPreview}
                    className="mt-2 inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-[#4d6f67] hover:bg-[#eef3f1] lg:hidden"
                  >
                    View preview
                    <ArrowRight className="size-3" aria-hidden="true" />
                  </button>
                ) : null}
              </div>
            )}
          </div>
        </div>
      )}
    </article>
  );
}

function MobilePaneButton({
  active,
  busy = false,
  children,
  onClick,
}: {
  active: boolean;
  busy?: boolean;
  children: string;
  onClick: () => void;
}): ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={busy ? `${children}, updating` : undefined}
      className={cn(
        "inline-flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium",
        active ? "bg-[#eaf5fd] text-[#20272b]" : "text-[#73766f]",
      )}
    >
      {children}
      {busy ? <SignalWaitDot className="bg-current" /> : null}
    </button>
  );
}

function PreviewSizeButton({
  active,
  children,
  label,
  onClick,
}: {
  active: boolean;
  children: ReactElement;
  label: string;
  onClick: () => void;
}): ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={cn(
        "flex size-7 items-center justify-center rounded-md [&_svg]:size-3.5",
        active
          ? "bg-white text-[#2e3d39] shadow-sm"
          : "text-[#8b8d85] hover:text-[#4b4d47]",
      )}
    >
      {children}
    </button>
  );
}

function WorkspaceLoading(): ReactElement {
  return (
    <div className="flex h-full items-center justify-center gap-2 bg-[#f4f4f1] text-sm text-[#74766f]">
      <SignalWaitDot className="bg-[#74766f]" />
      Opening workspace…
    </div>
  );
}

function WorkspaceError({
  workspaceId,
  reason = "unavailable",
}: {
  workspaceId: string;
  reason?: WorkspaceErrorReason;
}): ReactElement {
  const unauthorized = reason === "unauthorized";
  const forbidden = reason === "forbidden";
  return (
    <div className="flex h-full items-center justify-center bg-[#f4f4f1] px-6">
      <div className="max-w-sm rounded-2xl border border-[#deded8] bg-white p-7 text-center shadow-sm">
        <h1 className="text-lg font-semibold">
          {unauthorized
            ? "Sign in to open this workspace"
            : forbidden
              ? "You don’t have access to this workspace"
              : "Workspace unavailable"}
        </h1>
        <p className="mt-2 text-sm leading-6 text-[#74766f]">
          {unauthorized
            ? "Use the email connected to this Chill project."
            : forbidden
              ? "Choose a workspace connected to your account."
              : "We couldn’t load this workspace just now."}
        </p>
        {reason === "unavailable" ? (
          <Button
            type="button"
            className="mt-5"
            onClick={() => window.location.reload()}
          >
            Try again
          </Button>
        ) : (
          <Button asChild className="mt-5">
            <a
              href={
                unauthorized
                  ? `/login?slug=${encodeURIComponent(workspaceId)}`
                  : "/workspaces"
              }
            >
              {unauthorized ? "Sign in" : "View your workspaces"}
            </a>
          </Button>
        )}
      </div>
    </div>
  );
}

function workspaceErrorMessage(error: unknown): string {
  if (error instanceof WorkspaceApiError) {
    if (error.status === 401) return "Your session expired. Sign in again.";
    if (error.status === 403)
      return "You don’t have permission to change this workspace.";
    if (error.status === 409)
      return "Chill is still finishing the current change.";
    if (error.message === "attachment_too_large") {
      return "Choose an image that is 4 MB or smaller.";
    }
    if (error.message === "unsupported_attachment") {
      return "Choose a JPEG, PNG, WebP, or GIF image.";
    }
    if (
      error.message === "empty_attachment" ||
      error.message === "invalid_attachment"
    ) {
      return "That image couldn’t be attached. Choose another one.";
    }
  }
  return "That message didn’t go through. Please try again.";
}

function continuationErrorMessage(error: unknown): string {
  if (error instanceof WorkspaceApiError) {
    if (error.status === 401) return "Your session expired. Sign in again.";
    if (error.status === 402) {
      return "Add credits to your balance, then continue from here.";
    }
    if (error.status === 409) {
      return "This continuation is already being handled. Refresh in a moment.";
    }
  }
  return "That continuation didn’t go through. Your saved work is unchanged.";
}

function safeAttachmentUrl(value: string | undefined): string | null {
  if (!value) return null;
  if (value.startsWith("blob:")) return value;
  try {
    const url = new URL(value);
    return url.protocol === "https:" || url.protocol === "http:"
      ? url.href
      : null;
  } catch {
    return null;
  }
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function useWorkspaceDraft(
  scope: string | null,
): readonly [string, (value: SetStateAction<string>) => void] {
  const storageKey = scope === null ? null : `chill:workspace-draft:${scope}`;
  const [state, setDraftState] = useState(() => ({ key: storageKey, draft: readWorkspaceDraft(storageKey) }));

  useEffect(() => {
    setDraftState({ key: storageKey, draft: readWorkspaceDraft(storageKey) });
  }, [storageKey]);

  const setDraft = useCallback(
    (value: SetStateAction<string>) => {
      setDraftState((current) => {
        const next = typeof value === "function" ? value(current.key === storageKey ? current.draft : "") : value;
        writeWorkspaceDraft(storageKey, next);
        return { key: storageKey, draft: next };
      });
    },
    [storageKey],
  );

  return [state.key === storageKey ? state.draft : "", setDraft] as const;
}

function readWorkspaceDraft(storageKey: string | null): string {
  if (storageKey === null) return "";
  try {
    return window.sessionStorage.getItem(storageKey) ?? "";
  } catch {
    return "";
  }
}

function writeWorkspaceDraft(storageKey: string | null, draft: string): void {
  if (storageKey === null) return;
  try {
    if (draft) window.sessionStorage.setItem(storageKey, draft);
    else window.sessionStorage.removeItem(storageKey);
  } catch {
    // The composer still works when browser storage is unavailable.
  }
}
